import { Fragment } from 'react';

import styled from 'styled-components';

import { Button } from '@components/button';
import { EmptyState } from '@components/empty-state';
import { HideContainer } from '@components/hide-container';
import { IconEdit } from '@components/icons/edit';
import { IconFormIndicatorWarning } from '@components/icons/form-indicator-warning';
import { IconIssues } from '@components/icons/issues';
import { IconOpen } from '@components/icons/open';
import { IconRatingGood } from '@components/icons/rating-good';
import { Separator } from '@components/separator';
import { Sidebar, useSidebar } from '@components/sidebar';
import { Stack } from '@components/stack';
import { Text } from '@components/text';
import { styledTranslate, useScopedTranslate } from '@locale/translate';
import { theme } from '@styles/theme';
import { pxToRem } from '@utils/converters';

import {
  TravellerActionError,
  TravellerJourneyDetails,
  TravellerWarning,
} from 'api/traveller-journeys/types';
import { ActionIcon } from 'ui/actions/action-icon';
import { ActionLabel } from 'ui/actions/action-label';
import { ActionErrorMessage } from 'ui/actions/common';
import {
  getHasTravellerActionSidebar,
  showActionSidebar,
} from 'ui/traveller-journey-builder/action-sidebar';
import { actionErrorTitle } from 'ui/traveller-journey-builder/node-types/common';

import { useCompanyTravellerToJourneySidebar } from './company-traveller-to-journey';

import { useCompanyTravellerSidebar } from '../company-travellers/sidebars/form';

type Traveller = Pick<TravellerJourneyDetails, 'companyTravellerShortId' | 'shortId'> & {
  journeyShortId: string;
};

export const useTravellerIssuesSidebar = () =>
  useSidebar(TravellerIssuesSidebar, {
    variant: 'REGULAR',
    closeOnClickOutside: true,
  });

const TravellerIssuesSidebar = ({
  traveller,
  warnings,
  errors,
}: {
  traveller: Traveller;
  warnings: TravellerWarning[];
  errors: TravellerActionError[];
}) => {
  const { t } = useScopedTranslate(locale);

  return (
    <Sidebar>
      <Sidebar.SimpleHeader title={t('issues')} icon={IconIssues} iconColor={'blue100'} />
      <Sidebar.Content>
        <Stack space={6} style={{ padding: theme.space[6] }}>
          {warnings.length === 0 && errors.length === 0 && (
            <EmptyState
              skipMargin
              variant="small"
              icon={<IconRatingGood size={40} />}
              header=""
              content={t('noIssuesDescription')}
            />
          )}
          <HideContainer
            title={t('error')}
            counter={errors.length}
            defaultClosed={errors.length === 0}
          >
            <Stack space={2}>
              {errors.map((action, idx) => (
                <Fragment key={action.shortId}>
                  <ActionError action={action} />
                </Fragment>
              ))}
            </Stack>
          </HideContainer>
          <Separator />
          <HideContainer
            title={t('warning')}
            counter={warnings.length}
            defaultClosed={warnings.length === 0}
          >
            <Stack space={2}>
              {warnings.map((variable) => (
                <Fragment key={variable.variable}>
                  <MissingVariable variable={variable} traveller={traveller} />
                </Fragment>
              ))}
            </Stack>
          </HideContainer>
        </Stack>
      </Sidebar.Content>
    </Sidebar>
  );
};

const ActionError = ({ action }: { action: TravellerActionError }) => {
  const { t } = useScopedTranslate(locale);
  const mainAction = action.groupParentAction ?? action;
  const canOpenSidebar = getHasTravellerActionSidebar(action.action);

  return (
    <MissingVariableContainer>
      <Stack space={4}>
        <ActionErrorContainer>
          <Stack orientation="horizontal" space={2} align="center">
            <ActionIcon action={mainAction.action} />
            <Stack>
              <ActionLabel font="semibold14" action={mainAction.action} />
              <Text
                color="textSecondary"
                font="regular14"
                text={action.description || t('noDescription')}
              />
            </Stack>
          </Stack>
          <Text font="regular14" color="textSecondary" text={mainAction.description} />
        </ActionErrorContainer>
        <ActionErrorMessage
          errorMessage={{
            title: actionErrorTitle[action.action],
            description: action.result?.message,
            date: action.result?.dateFailed,
          }}
        />
        {canOpenSidebar && (
          <div>
            <Button
              variant="secondary"
              iconLeft={<IconOpen />}
              text={t('openAction')}
              onClick={() => showActionSidebar({ id: mainAction.shortId, type: mainAction.action })}
            />
          </div>
        )}
      </Stack>
    </MissingVariableContainer>
  );
};

const MissingVariable = ({
  variable,
  traveller,
}: {
  variable: TravellerWarning;
  traveller: Traveller;
}) => {
  const { t } = useScopedTranslate(locale);
  const showEditTravellerSidebar = useCompanyTravellerToJourneySidebar();
  const showEditTravellerAttributesSidebar = useCompanyTravellerSidebar();

  return (
    <MissingVariableContainer>
      <Stack space={2}>
        <Stack space={2} orientation="horizontal" align="center">
          <IconFormIndicatorWarning />
          <Text font="semibold14" text={variable.label} />
        </Stack>
        <Text
          font="regular14"
          text={styledTranslate({
            key: 'variableNotSet',
            variables: {
              variableLabel: {
                text: variable.label,
                font: 'regular14',
              },
              actionsCount: {
                text: `${variable.count} ${variable.count > 1 ? 'actions' : 'action'}`,
                color: 'elementBlue100',
                font: 'semibold14',
              },
            },
            translations: locale,
          })}
        />
        <div>
          <Button
            iconLeft={<IconEdit />}
            variant="secondary"
            text={t('editTravellerDetails')}
            onClick={() => {
              return variable.variable.includes('company_traveller')
                ? showEditTravellerAttributesSidebar({
                    companyTravellerShortId: traveller.companyTravellerShortId,
                  })
                : showEditTravellerSidebar({
                    journeyShortId: traveller.journeyShortId,
                    journeyTravellerId: traveller.shortId,
                  });
            }}
          />
        </div>
      </Stack>
    </MissingVariableContainer>
  );
};

const IssueContainer = styled.div({
  display: 'flex',
  gap: theme.space[2],
  borderWidth: pxToRem(1),
  borderStyle: 'solid',
  borderColor: theme.color.border10,
  borderRadius: theme.space[2],
});

const MissingVariableContainer = styled(IssueContainer)({
  flexDirection: 'column',
  backgroundColor: theme.color.uiTrans5,
  padding: theme.space[4],
});

const ActionErrorContainer = styled(IssueContainer)({
  alignItems: 'center',
  minHeight: '56px',
  padding: `${pxToRem(8)} ${pxToRem(8)} ${pxToRem(8)} ${pxToRem(16)}`,
  backgroundColor: theme.color.backgroundLightDark,
});

const locale = {
  en: {
    noDescription: 'No description',
    noIssues: 'No issues',
    issues: 'Issues',
    noIssuesDescription:
      'There are no issues with this traveller and the journey should run normally',
    variableNotSet:
      '{variableLabel} is used for {actionsCount} in this journey and will not complete unless it is set',
    editTravellerDetails: 'Edit traveller details',
    openAction: 'Open action',
    error: 'ERROR',
    warning: 'WARNING',
  },
};
